import React, { useState, useEffect } from 'react';
import { getInsurance, getCountryConfig, calculateComparison, exportComparisonPDF, exportComparisonExcel } from '../services/api';
import toast from 'react-hot-toast';

const COUNTRIES = ['Saudi Arabia', 'India', 'Australia', 'United States', 'Netherlands', 'Japan', 'Norway', 'China'];
const FREQUENCIES = ['Per Hour','Per Day','Per Week','Per Month','Per Year'];
const CURRENCIES  = ['USD','GBP','EUR','SAR','INR','AUD','AED','QAR','JPY','CNY','NOK'];
const AUS_STATES  = ['New South Wales','Victoria','Australian Capital Territory','Queensland','South Australia','Northern Territory','Western Australia'];
const VISA_TYPES  = ['None','AP Iqama','China Harbour Iqama','Work Permit'];
const CHINA_CITIES = ['Guangzhou','Dajin','Qinzhou','Zhongshan','Qingdao','Hefei','Shanghai','Nantong','Yantai','Beijing','Xian','Tianjin'];
const NORWAY_SS_ZONES = ['Zone 1','Zone 2','Zone 3','Zone 4','Zone 5','Expat from USA/Canada'];
const NORWAY_TAX_METHODS = ['Top Rate','Actual Tax'];
const NL_CONTRACT_TYPES = ['Temporary','Indefinite'];
const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NH','NJ','NM','NV','NY','OH','OK','OR','PA','SC','SD','TN','TX','USVI','UT','VA','VT','WA','WI','WV','WY'];

const fmt  = (v,d=2) => v==null ? '—' : parseFloat(v).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d});
const fmtP = v => v==null ? '—' : (parseFloat(v)*100).toFixed(2)+'%';

const COUNTRY_COLOURS = {
  'Saudi Arabia':  { header:'#1e3a5f', light:'#f0f4ff', badge:'#dbeafe', badgeText:'#1e40af' },
  'India':         { header:'#065f46', light:'#f0fdf4', badge:'#d1fae5', badgeText:'#065f46' },
  'Australia':     { header:'#92400e', light:'#fffbeb', badge:'#fef3c7', badgeText:'#92400e' },
  'United States': { header:'#1d4ed8', light:'#eff6ff', badge:'#bfdbfe', badgeText:'#1d4ed8' },
  'Netherlands':   { header:'#b45309', light:'#fff7ed', badge:'#fed7aa', badgeText:'#9a3412' },
  'Japan':         { header:'#9f1239', light:'#fff1f2', badge:'#fecdd3', badgeText:'#9f1239' },
  'Norway':        { header:'#1e40af', light:'#eff6ff', badge:'#c7d2fe', badgeText:'#3730a3' },
  'China':         { header:'#7f1d1d', light:'#fef2f2', badge:'#fecaca', badgeText:'#7f1d1d' },
};

const defaultCountrySettings = {
  'Saudi Arabia':  { invoiceFromKSA:true, includeEOSB:true, includeMOL:false, includeVisa:false, visaType:'None', includeMob:false },
  'India':         { inApplyGst:false, inTaxMethod:'Top Rate' },
  'Australia':     { stateTerritory:'Western Australia', auApplyGst:false, auTaxMethod:'Top Rate' },
  'United States': { usState:'AL', usVmsEnabled:false, usSutaOffsetsFuta:'Yes', medicalInsuranceName:'US Medical - New', usTaxMethod:'Top Rate' },
  'Netherlands':   { nlContractType:'Temporary', nlApplyVat:false, nlBurdensCapped:true, nlIncludeWaitingDay:false, nlTravelAllowance:false, nlTravelRatePerKm:0, nlKmPerDay:0, nlTaxMethod:'Top Rate', nl30PctRuling:true },
  'Japan':         { jpApplyConsumptionTax:false, jpTaxMethod:'Top Rate' },
  'Norway':        { noSsZone:'Zone 1', noTaxMethod:'Top Rate', noApplyVat:false, contractorType:'Local' },
  'China':         { chinaCity:'Beijing', cnApplyVat:false, cnTaxMethod:'Top Rate' },
};

function CountryMultiSelect({ selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = c => {
    if (selected.includes(c)) {
      if (selected.length === 1) return;
      onChange(selected.filter(x => x !== c));
    } else {
      onChange([...selected, c]);
    }
  };

  const displayText = selected.length === 0 ? 'Select countries...' : selected.join(', ');

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <div onClick={() => setOpen(o => !o)}
        className="form-control"
        style={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', userSelect:'none', minHeight:40 }}>
        <span style={{ color: selected.length === 0 ? 'var(--muted)' : 'var(--text)', fontSize:13.5 }}>{displayText}</span>
        <span style={{ color:'var(--muted)', fontSize:11, marginLeft:8, flexShrink:0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, background:'var(--surface)', border:'1.5px solid var(--accent)', borderTop:'none', borderRadius:'0 0 var(--r-sm) var(--r-sm)', boxShadow:'0 4px 12px rgba(0,0,0,.12)', overflow:'hidden' }}>
          {COUNTRIES.map(c => {
            const isOn = selected.includes(c);
            return (
              <div key={c} onMouseDown={e => { e.preventDefault(); toggle(c); }}
                style={{ padding:'10px 14px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', background: isOn ? 'var(--surface2)' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = isOn ? 'var(--surface2)' : 'transparent'}>
                <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`, background: isOn ? 'var(--primary)' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .12s' }}>
                  {isOn && <span style={{ color:'#fff', fontSize:10, lineHeight:1 }}>✓</span>}
                </div>
                <span style={{ fontSize:13.5, fontWeight: isOn ? 600 : 400, color:'var(--text)' }}>{c}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ComparisonPage() {
  const [ins,      setIns]      = useState([]);
  const [vmsFeePct, setVmsFeePct] = useState(null);
  const [selected, setSelected] = useState(['Saudi Arabia','India','Australia']);
  const [loading,  setLoading]  = useState(false);
  const [results,  setResults]  = useState(null);
  const [stale,    setStale]    = useState(false);
  const [exporting, setExporting] = useState(null);

  const [f, setF] = useState({
    billRate:'', payFrequency:'Per Day', billingFrequency:'Per Day',
    rateType:'gross', contractorType:'Expat',
    inputCurrency:'USD', outputCurrency:'USD',
    medicalInsuranceName:'',
  });

  const [countrySettings, setCountrySettings] = useState({ ...defaultCountrySettings });

  const set    = (k,v) => { setF(p => ({...p,[k]:v})); setStale(true); };
  const setCfg = (country,k,v) => { setCountrySettings(p => ({...p,[country]:{...p[country],[k]:v}})); setStale(true); };

  useEffect(() => {
    getInsurance().then(r => setIns(r.data)).catch(()=>{});
    getCountryConfig('United States').then(({data}) => {
      const row = data.find(d => d.param_key === 'vms_fee_pct');
      setVmsFeePct(row ? parseFloat(row.param_value) : null);
    }).catch(()=>{});
  },[]);

  const buildExportPayload = () => ({
    countries: selected.map(country => ({ country, countryInputs: countrySettings[country] || {} })),
    inputs: {
      billRate: parseFloat(f.billRate), payFrequency: f.payFrequency,
      billingFrequency: f.billingFrequency, rateType: f.rateType,
      contractorType: f.contractorType, inputCurrency: f.inputCurrency,
      outputCurrency: f.outputCurrency, medicalInsuranceName: f.medicalInsuranceName,
    },
  });

  const handleExport = async (type) => {
    setExporting(type);
    try {
      if (type === 'pdf')   await exportComparisonPDF(buildExportPayload());
      if (type === 'excel') await exportComparisonExcel(buildExportPayload());
    } catch { toast.error('Export failed'); }
    finally { setExporting(null); }
  };

  const handleCompare = async () => {
    if (!f.billRate) return toast.error('Enter a pay rate');
    if (selected.length < 2) return toast.error('Select at least 2 countries to compare');
    setLoading(true);
    setStale(false);
    setResults(null);
    try {
      const promises = selected.map(country =>
        calculateComparison({
          country,
          inputs: {
            billRate:            parseFloat(f.billRate),
            payFrequency:        f.payFrequency,
            billingFrequency:    f.billingFrequency,
            rateType:            f.rateType,
            contractorType:      f.contractorType,
            inputCurrency:       f.inputCurrency,
            outputCurrency:      f.outputCurrency,
            medicalInsuranceName:f.medicalInsuranceName,
            ...(countrySettings[country] || {}),
          },
        }).then(r => ({ country, ...r.data }))
         .catch(e => ({ country, error: e.response?.data?.message || 'Failed' }))
      );
      const res = await Promise.all(promises);
      setResults(res);
      const errors = res.filter(r => r.error);
      if (errors.length) toast.error(`${errors.map(e=>e.country).join(', ')}: calculation failed`);
      else toast.success('Comparison ready');
    } finally { setLoading(false); }
  };

  const cur = f.outputCurrency;
  const bf  = f.billingFrequency.replace('Per ','');

  return (
    <div style={{ display:'grid', gridTemplateColumns:'360px 1fr', gap:22, height:'calc(100vh - 72px)', alignItems:'stretch' }}>

        {/* INPUT PANEL */}
        <div style={{ display:'flex', flexDirection:'column', minHeight:0 }}>

          {/* Title — inside left column, never scrolls */}
          <div style={{ paddingLeft:2, paddingBottom:14, flexShrink:0 }}>
            <h1 className="page-title" style={{ marginBottom:2 }}>🌍 Multi-Country Comparison</h1>
            <p className="page-sub" style={{ marginBottom:0 }}>Compare contractor costs across countries side by side</p>
          </div>

          {/* Scrollable cards */}
          <div style={{ display:'flex', flexDirection:'column', gap:14, overflowY:'auto', paddingRight:4, flex:1, minHeight:0 }}>

          <div className="card">
            <div className="card-header"><span style={{ fontWeight:600, color:'var(--text)', fontSize:13.5 }}>📋 Common Inputs</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Countries to Compare</label>
                <CountryMultiSelect selected={selected} onChange={s => { setSelected(s); setStale(true); }} />
                {selected.length < 2 && (
                  <div style={{ fontSize:11.5, color:'var(--warning)', marginTop:4 }}>Select at least 2 countries to compare</div>
                )}
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Pay Rate</label>
                  <input className="form-control" type="number" placeholder="0.00" value={f.billRate} onChange={e => { set('billRate',e.target.value); }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pay Frequency</label>
                  <select className="form-control" value={f.payFrequency} onChange={e => { set('payFrequency',e.target.value); }}>
                    {FREQUENCIES.map(x => <option key={x}>{x}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bill Frequency</label>
                <select className="form-control" value={f.billingFrequency} onChange={e => { set('billingFrequency',e.target.value); }}>
                  {FREQUENCIES.map(x => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Rate Type</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[['gross','Gross'],['net','Net']].map(([val,lbl]) => (
                    <button key={val} type="button" onClick={() => { set('rateType',val); }}
                      className="btn" style={{ flex:1, justifyContent:'center', padding:'8px 12px',
                        background: f.rateType===val ? 'var(--primary)' : 'var(--surface2)',
                        color: f.rateType===val ? '#fff' : 'var(--text)',
                        border: f.rateType===val ? 'none' : '1.5px solid var(--border)',
                        fontWeight: f.rateType===val ? 600 : 400 }}>
                      {lbl} Rate
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Contractor Type</label>
                  <select className="form-control" value={f.contractorType} onChange={e => { set('contractorType',e.target.value); }}>
                    <option>Expat</option>
                    <option>Local</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Input Currency</label>
                  <select className="form-control" value={f.inputCurrency} onChange={e => { set('inputCurrency',e.target.value); }}>
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Output Currency</label>
                <select className="form-control" value={f.outputCurrency} onChange={e => { set('outputCurrency',e.target.value); }}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Medical Insurance</label>
                <select className="form-control" value={f.medicalInsuranceName} onChange={e => { set('medicalInsuranceName',e.target.value); }}>
                  {selected.includes('Japan') && selected.length === 1 && <option value="">— Select insurance plan —</option>}
                  <option value="None">None</option>
                  <option value="Percentage">Percentage of Rate</option>
                  {ins.map(p => <option key={p.id} value={p.name}>{p.name} ({p.currency_code} {fmt(p.amount)} /{p.frequency.replace('Per ','')})</option>)}
                </select>
                {selected.includes('Japan') && (
                  <div style={{ marginTop:7, padding:'7px 10px', background:'#fefce8', borderRadius:'var(--r)', border:'1px solid #fef08a', fontSize:12, color:'#854d0e', display:'flex', alignItems:'center', gap:6 }}>
                    <span>⚠</span>
                    <span>Japan applies <strong>0% medical insurance</strong> by default.</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span style={{ fontWeight:600, color:'var(--text)', fontSize:13.5 }}>⚙️ Country Specific Settings</span></div>
            <div className="card-body">
              {selected.includes('Saudi Arabia') && (
                <div style={{ marginBottom:12, padding:'10px 12px', background:'#f0f4ff', borderRadius:'var(--r-sm)', border:'1px solid #dbeafe' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#1e40af', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Saudi Arabia Settings</div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Invoice from KSA?</label>
                    <select className="form-control" value={countrySettings['Saudi Arabia'].invoiceFromKSA ? 'yes' : 'no'}
                      onChange={e => { setCfg('Saudi Arabia','invoiceFromKSA',e.target.value==='yes'); }}>
                      <option value="yes">Yes — VAT 15%</option>
                      <option value="no">No — WHT 15%</option>
                    </select>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {[['includeEOSB','EOSB'],['includeMOL','MOL'],['includeMob','Mob Costs'],['includeVisa','Visa']].map(([k,lbl]) => (
                      <label key={k} style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, cursor:'pointer' }}>
                        <input type="checkbox" checked={countrySettings['Saudi Arabia'][k]||false}
                          onChange={e => { setCfg('Saudi Arabia',k,e.target.checked); }}
                          style={{ accentColor:'var(--primary)' }} />
                        {lbl}
                      </label>
                    ))}
                  </div>
                  {countrySettings['Saudi Arabia'].includeVisa && (
                    <div className="form-group" style={{ marginTop:8, marginBottom:0 }}>
                      <label className="form-label">Visa Type</label>
                      <select className="form-control" value={countrySettings['Saudi Arabia'].visaType||'None'}
                        onChange={e => { setCfg('Saudi Arabia','visaType',e.target.value); }}>
                        {VISA_TYPES.map(v => <option key={v}>{v}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {selected.includes('India') && (
                <div style={{ marginBottom:12, padding:'10px 12px', background:'#fdf4ff', borderRadius:'var(--r-sm)', border:'1px solid #e9d5ff' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#6b21a8', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>India Settings</div>

                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['India']?.inTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('India','inTaxMethod',e.target.value); }}>
                      <option value="Top Rate">Top Rate</option>
                      <option value="Actual Tax">Actual Tax</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['India']?.inApplyGst)}
                        onChange={e => { setCfg('India','inApplyGst',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply GST (Goods and Services Tax)</span>
                    </label>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 18%</div>
                  </div>
                </div>
              )}

              {selected.includes('Australia') && (
                <div style={{ padding:'10px 12px', background:'#fffbeb', borderRadius:'var(--r-sm)', border:'1px solid #fef3c7' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#92400e', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Australia Settings</div>

                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['Australia']?.auTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('Australia','auTaxMethod',e.target.value); }}>
                      <option value="Top Rate">Top Rate</option>
                      <option value="Actual Tax">Actual Tax</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">State / Territory</label>
                    <select className="form-control" value={countrySettings['Australia'].stateTerritory}
                      onChange={e => { setCfg('Australia','stateTerritory',e.target.value); }}>
                      {AUS_STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['Australia']?.auApplyGst)}
                        onChange={e => { setCfg('Australia','auApplyGst',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply GST (Goods and Services Tax)</span>
                    </label>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 10%</div>
                  </div>
                </div>
              )}
              {selected.includes('United States') && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'#f0fdf4', borderRadius:'var(--r-sm)', border:'1px solid #bbf7d0' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#065f46', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>United States Settings</div>

                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['United States']?.usTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('United States','usTaxMethod',e.target.value); }}>
                      <option value="Top Rate">Top Rate</option>
                      <option value="Actual Tax">Actual Tax</option>
                    </select>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Actual Tax calculation coming soon</div>
                  </div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Medical Insurance</label>
                    <select className="form-control" value={countrySettings['United States']?.medicalInsuranceName || 'US Medical - New'}
                      onChange={e => { setCfg('United States','medicalInsuranceName',e.target.value); }}>
                      {ins.map(i => <option key={i.name}>{i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">State</label>
                    <select className="form-control" value={countrySettings['United States']?.usState || 'AL'}
                      onChange={e => { setCfg('United States','usState',e.target.value); }}>
                      {US_STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">VMS Fee</label>
                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['United States']?.usVmsEnabled)}
                        onChange={e => { setCfg('United States','usVmsEnabled',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply VMS Fee</span>
                    </label>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>
                      Current VMS fee for United States is {vmsFeePct != null ? `${(vmsFeePct * 100).toFixed(2)}%` : '—'}
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">SUTA Offsets FUTA?</label>
                    <select className="form-control" value={countrySettings['United States']?.usSutaOffsetsFuta || 'Yes'}
                      onChange={e => { setCfg('United States','usSutaOffsetsFuta',e.target.value); }}>
                      <option value="Yes">Yes — FUTA reduced by SUTA credit</option>
                      <option value="No">No — FUTA at full 6%</option>
                    </select>
                  </div>
                </div>
              )}

              {selected.includes('Norway') && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'#eff6ff', borderRadius:'var(--r-sm)', border:'1px solid #c7d2fe' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#3730a3', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Norway Settings</div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Social Security Zone</label>
                    <select className="form-control" value={countrySettings['Norway']?.noSsZone || 'Zone 1'}
                      onChange={e => { setCfg('Norway','noSsZone',e.target.value); }}>
                      {NORWAY_SS_ZONES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['Norway']?.noTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('Norway','noTaxMethod',e.target.value); }}>
                      {NORWAY_TAX_METHODS.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['Norway']?.noApplyVat)}
                        onChange={e => { setCfg('Norway','noApplyVat',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply MVA (VAT)</span>
                    </label>
                    {countrySettings['Norway']?.noApplyVat && (
                      <div style={{ marginTop:6, padding:'6px 10px', background:'#fefce8', borderRadius:'var(--r)', border:'1px solid #fef08a', fontSize:12, color:'#854d0e' }}>
                        25% MVA (VAT) will be applied to the invoice value.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {selected.includes('Netherlands') && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'#fff7ed', borderRadius:'var(--r-sm)', border:'1px solid #fed7aa' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9a3412', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Netherlands Settings</div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['Netherlands']?.nlTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('Netherlands','nlTaxMethod',e.target.value); }}>
                      <option value="Top Rate">Top Rate</option>
                      <option value="Actual Tax">Actual Tax</option>
                    </select>
                  </div>
                  {countrySettings['Netherlands']?.nlTaxMethod === 'Actual Tax' && (
                    <div className="form-group" style={{ marginBottom:8 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                        <input type="checkbox"
                          checked={!!(countrySettings['Netherlands']?.nl30PctRuling)}
                          onChange={e => { setCfg('Netherlands','nl30PctRuling',e.target.checked); }} />
                        <span style={{ fontSize:13 }}>Apply 30% Ruling (Expats)</span>
                      </label>
                      <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>30% of gross salary is tax-free for qualifying expats</div>
                    </div>
                  )}

                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input type="checkbox" checked={!!(countrySettings['Netherlands']?.nlBurdensCapped)}
                        onChange={e => { setCfg('Netherlands','nlBurdensCapped',e.target.checked); }} />
                      <span>Employer Burdens Capped (R76)</span>
                    </label>
                  </div>
                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input type="checkbox" checked={!!(countrySettings['Netherlands']?.nlTravelAllowance)}
                        onChange={e => { setCfg('Netherlands','nlTravelAllowance',e.target.checked); }} />
                      <span>Travel Allowance (R69)</span>
                    </label>
                  </div>
                  {countrySettings['Netherlands']?.nlTravelAllowance && (<>
                    <div className="form-group" style={{ marginBottom:8 }}>
                      <label className="form-label">Rate per km</label>
                      <input type="number" className="form-control" value={countrySettings['Netherlands']?.nlTravelRatePerKm || 0}
                        onChange={e => { setCfg('Netherlands','nlTravelRatePerKm', parseFloat(e.target.value)||0); }} step="0.01" />
                    </div>
                    <div className="form-group" style={{ marginBottom:8 }}>
                      <label className="form-label">km per day</label>
                      <input type="number" className="form-control" value={countrySettings['Netherlands']?.nlKmPerDay || 0}
                        onChange={e => { setCfg('Netherlands','nlKmPerDay', parseFloat(e.target.value)||0); }} step="0.1" />
                    </div>
                  </>)}

                  <div className="form-group" style={{ marginBottom:0 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['Netherlands']?.nlApplyVat)}
                        onChange={e => { setCfg('Netherlands','nlApplyVat',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply BTW (VAT)</span>
                    </label>
                    {countrySettings['Netherlands']?.nlApplyVat && (
                      <div style={{ marginTop:6, padding:'6px 10px', background:'#fefce8', borderRadius:'var(--r)', border:'1px solid #fef08a', fontSize:12, color:'#854d0e' }}>
                        21% BTW (VAT) will be applied to the invoice value.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {selected.includes('Japan') && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'#fff7ed', borderRadius:'var(--r-sm)', border:'1px solid #fed7aa' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9a3412', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>Japan Settings</div>

                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['Japan']?.jpTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('Japan','jpTaxMethod',e.target.value); }}>
                      <option value="Top Rate">Top Rate</option>
                      <option value="Actual Tax">Actual Tax</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['Japan']?.jpApplyConsumptionTax)}
                        onChange={e => { setCfg('Japan','jpApplyConsumptionTax',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply JCT (Consumption Tax)</span>
                    </label>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 10% since Oct 2019</div>
                  </div>
                </div>
              )}
              {selected.includes('China') && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'#fef2f2', borderRadius:'var(--r-sm)', border:'1px solid #fecaca' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#991b1b', marginBottom:8, textTransform:'uppercase', letterSpacing:'.05em' }}>China Settings</div>

                  <div className="form-group" style={{ marginBottom:8 }}>
                    <label className="form-label">Tax Method</label>
                    <select className="form-control" value={countrySettings['China']?.cnTaxMethod || 'Top Rate'}
                      onChange={e => { setCfg('China','cnTaxMethod',e.target.value); }}>
                      <option value="Top Rate">Top Rate</option>
                      <option value="Actual Tax">Actual Tax</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">City / Region</label>
                    <select className="form-control" value={countrySettings['China']?.chinaCity || 'Beijing'}
                      onChange={e => { setCfg('China','chinaCity',e.target.value); }}>
                      {CHINA_CITIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom:0, marginTop:8 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!(countrySettings['China']?.cnApplyVat)}
                        onChange={e => { setCfg('China','cnApplyVat',e.target.checked); }} />
                      <span style={{ fontSize:13 }}>Apply VAT</span>
                    </label>
                    <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Standard rate 13% since April 2019</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          </div>{/* end scrollable cards */}

          {/* Compare button — pinned at bottom */}
          <div style={{ paddingTop:12, flexShrink:0 }}>
            <button className="btn btn-primary btn-lg" onClick={handleCompare} disabled={loading}
              style={{ justifyContent:'center', width:'100%' }}>
              {loading ? <><div className="spinner"/>Calculating...</> : '⚡ Compare Countries'}
            </button>
          </div>

        </div>{/* end left panel */}

        {/* RESULTS PANEL */}
        <div style={{ minHeight:0, overflowY:'auto', height:'100%' }}>
        {!results ? (
          <div className="card" style={{ height:'100%', boxSizing:'border-box', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'40px' }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🌍</div>
            <div style={{ fontSize:17, fontWeight:600, color:'var(--text)', marginBottom:8 }}>Ready to Compare</div>
            <div style={{ fontSize:13.5, color:'var(--text2)' }}>Select countries, fill in the inputs and click Compare Countries</div>
          </div>
        ) : (() => {
          const ok  = results.filter(r => !r.error);
          const err = results.filter(r => r.error);
          const header = (
            <div className="card-header">
              <div>
                <div style={{ fontWeight:700, color:'var(--text)', fontSize:14.5 }}>Cost Breakdown Comparison</div>
                <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>
                  {cur} · {f.billingFrequency} · {f.contractorType} · {f.rateType} rate
                </div>
              </div>
            </div>
          );

          const ROWS = [
            { label: `Bill Rate / ${bf}`,         key: r => r.billRateDisplay ?? r.billRatePerDay,     style:'normal'   },
            { label: 'Gross Rate to Man',          key: r => r.breakdown?.grossRateToMan?.value,        style:'subtotal' },
            { label: 'In-Country Burdens',         key: r => r.breakdown?.burdens?.total,               style:'burden'   },
            { label: 'Gross Rate Post Burdens',    key: r => r.breakdown?.grossRatePostBurdens?.value,  style:'subtotal' },
            { label: `Invoice Pre Tax / ${bf}`,    key: r => r.breakdown?.invoicePreTax?.value,         style:'subtotal' },
            { label: 'Tax',                        key: r => r.breakdown?.tax?.value,
              sub:  r => r.breakdown?.tax?.label,                                                       style:'tax'      },
            { label: `Invoice Post Tax / ${bf}`,   key: r => r.breakdown?.invoicePostTax?.value,        style:'total'    },
            { label: 'Multiplier (Post Tax)',       key: r => null, mult: r => r.multiplierPostTax,     style:'mult'     },
          ];

          return (
            <div>
              {stale && (
                <div style={{ marginBottom:12, padding:'9px 16px', background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:'var(--r-sm)', fontSize:13, color:'#92400e', display:'flex', alignItems:'center', gap:8 }}>
                  ⚠️ Inputs have changed — click Compare Countries to update results.
                </div>
              )}

              {/* Export buttons */}
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginBottom:16 }}>
                <button className="btn btn-outline" onClick={() => handleExport('pdf')} disabled={!!exporting}
                  style={{ fontSize:13, padding:'7px 16px' }}>
                  {exporting === 'pdf' ? <><div className="spinner" style={{borderTopColor:'var(--text2)'}}/> Exporting...</> : <><i className="ti ti-file-type-pdf" aria-hidden="true"/>Export PDF</>}
                </button>
                <button className="btn btn-outline" onClick={() => handleExport('excel')} disabled={!!exporting}
                  style={{ fontSize:13, padding:'7px 16px' }}>
                  {exporting === 'excel' ? <><div className="spinner" style={{borderTopColor:'var(--text2)'}}/> Exporting...</> : <><i className="ti ti-file-spreadsheet" aria-hidden="true"/>Export Excel</>}
                </button>
              </div>

              {ok.length === 2 && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
                  {ok.map(r => {
                    const cc = COUNTRY_COLOURS[r.country] || { header:'#334155', light:'#f8fafc', badge:'#e2e8f0', badgeText:'#334155' };
                    return (
                      <div key={r.country} style={{ background:cc.header, borderRadius:'var(--r)', padding:'16px 20px' }}>
                        <div style={{ fontWeight:700, fontSize:15, color:'#fff', marginBottom:2 }}>{r.country}</div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,.65)', marginBottom:10 }}>
                          {r.contractorType} · {r.inputCurrency} → {r.currency}
                        </div>
                        <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>
                          {cur} {fmt(r.breakdown?.invoicePostTax?.value)}
                        </div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,.7)', marginTop:2 }}>Invoice / {bf} (Post Tax)</div>
                        <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid rgba(255,255,255,.2)', display:'flex', justifyContent:'space-between' }}>
                          <div>
                            <div style={{ fontSize:10, color:'rgba(255,255,255,.55)', textTransform:'uppercase' }}>Multiplier</div>
                            <div style={{ fontSize:16, fontWeight:700, color:'#fff' }}>{r.multiplierPostTax}x</div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:10, color:'rgba(255,255,255,.55)', textTransform:'uppercase' }}>Pre-tax</div>
                            <div style={{ fontSize:16, fontWeight:700, color:'rgba(255,255,255,.85)' }}>{r.multiplierPreTax}x</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ overflowX:'auto' }}><div className="card" style={{ width:'max-content', minWidth:'100%' }}>
                {header}
                <div>

                  {ok.length === 2 && (
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13.5 }}>
                      <thead>
                        <tr>
                          <th style={{ padding:'10px 16px', textAlign:'left', background:'var(--surface2)', color:'var(--text2)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', width:'34%' }}>
                            Item
                          </th>
                          {ok.map(r => {
                            const cc = COUNTRY_COLOURS[r.country] || { header:'#334155', light:'#f8fafc', badge:'#e2e8f0', badgeText:'#334155' };
                            return (
                              <th key={r.country} style={{ padding:'10px 16px', textAlign:'right', background:cc.header, color:'#fff', fontSize:13, fontWeight:700 }}>
                                {r.country}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {ROWS.map((row, i) => {
                          const isTotal    = row.style === 'total';
                          const isSubtotal = row.style === 'subtotal';
                          const isBurden   = row.style === 'burden';
                          const isMult     = row.style === 'mult';
                          const bg = isTotal ? '#1e3a5f' : isSubtotal ? '#dbeafe' : isBurden ? '#f0f7ff' : i%2===0 ? 'transparent' : 'var(--surface2)';
                          return (
                            <tr key={row.label} style={{ background: bg }}>
                              <td style={{ padding:'9px 16px', fontWeight: isTotal||isSubtotal||isBurden ? 600 : 400, color: isTotal ? '#fff' : 'var(--text)', fontSize: isTotal ? 14 : 13.5 }}>
                                {row.label}
                              </td>
                              {ok.map(r => {
                                const val  = row.key(r);
                                const mult = row.mult ? row.mult(r) : null;
                                const sub  = row.sub ? row.sub(r) : null;
                                const cc   = COUNTRY_COLOURS[r.country];
                                return (
                                  <td key={r.country} style={{ padding:'9px 16px', textAlign:'right', fontWeight: isTotal||isSubtotal||isBurden ? 600 : 400, color: isTotal ? '#fff' : isBurden ? cc.badgeText : 'var(--text)', fontSize: isTotal ? 14 : 13.5 }}>
                                    {isMult
                                      ? <span style={{ color:'var(--accent)', fontWeight:700 }}>{mult}x</span>
                                      : <>
                                          <div>{cur} {fmt(val)}</div>
                                          {sub && <div style={{ fontSize:11, color: isTotal ? 'rgba(255,255,255,.6)' : 'var(--muted)', fontWeight:400 }}>{sub}</div>}
                                        </>
                                    }
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {ok.length > 2 && (
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                      <thead>
                        <tr style={{ background:'#1e3a5f' }}>
                          <th style={{ padding:'10px 14px', textAlign:'left', color:'rgba(255,255,255,.75)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', minWidth:130 }}>
                            Country
                          </th>
                          {[`Bill Rate / ${bf}`,'Gross Rate to Man','In-Country Burdens','Gross Rate Post Burdens',`Invoice Pre Tax / ${bf}`,'Tax',`Invoice Post Tax / ${bf}`,'Multiplier'].map(col => (
                            <th key={col} style={{ padding:'10px 14px', textAlign:'right', color:'rgba(255,255,255,.75)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', whiteSpace:'nowrap' }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ok.map((r, i) => {
                          const cc = COUNTRY_COLOURS[r.country] || { header:'#334155', light:'#f8fafc', badge:'#e2e8f0', badgeText:'#334155' };
                          const bd = r.breakdown;
                          return (
                            <tr key={r.country} style={{ background: i%2===0 ? 'transparent' : 'var(--surface2)' }}>
                              <td style={{ padding:'11px 14px', fontWeight:700, whiteSpace:'nowrap' }}>
                                <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:12, fontWeight:600, background:cc.badge, color:cc.badgeText }}>
                                  {r.country}
                                </span>
                              </td>
                              <td style={{ padding:'11px 14px', textAlign:'right' }}>{cur} {fmt(r.billRateDisplay ?? r.billRatePerDay)}</td>
                              <td style={{ padding:'11px 14px', textAlign:'right', color:'var(--text)', fontWeight:600 }}>{cur} {fmt(bd?.grossRateToMan?.value)}</td>
                              <td style={{ padding:'11px 14px', textAlign:'right', color:cc.badgeText, fontWeight:600 }}>{cur} {fmt(bd?.burdens?.total)}</td>
                              <td style={{ padding:'11px 14px', textAlign:'right', color:'var(--text)', fontWeight:600 }}>{cur} {fmt(bd?.grossRatePostBurdens?.value)}</td>
                              <td style={{ padding:'11px 14px', textAlign:'right' }}>{cur} {fmt(bd?.invoicePreTax?.value)}</td>
                              <td style={{ padding:'11px 14px', textAlign:'right' }}>
                                <div>{cur} {fmt(bd?.tax?.value)}</div>
                                {bd?.tax?.label && <div style={{ fontSize:11, color:'var(--muted)' }}>{bd.tax.label}</div>}
                              </td>
                              <td style={{ padding:'11px 14px', textAlign:'right', background:cc.badge, color:cc.badgeText, fontWeight:700, fontSize:14 }}>{cur} {fmt(bd?.invoicePostTax?.value)}</td>
                              <td style={{ padding:'11px 14px', textAlign:'right', fontWeight:700, color:'var(--accent)' }}>
                                {r.multiplierPostTax}x
                                <div style={{ fontSize:11, color:'var(--muted)', fontWeight:400 }}>pre: {r.multiplierPreTax}x</div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                </div>
              </div></div>

              {err.map(r => (
                <div key={r.country} style={{ marginTop:10, padding:'10px 16px', background:'#fee2e2', borderRadius:'var(--r-sm)', fontSize:13, color:'#991b1b' }}>
                  ⚠️ {r.country}: {r.error}
                </div>
              ))}
            </div>
          );
        })()
        }
        </div>
    </div>
  );
}